import { memo, useState, useEffect, useRef, useCallback } from "react";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import { Image } from "../Image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faKey,
  faFileLines,
  faDisplay,
} from "@fortawesome/free-solid-svg-icons";

/* ---------------- WRAPPER ---------------- */

const Wrapper = styled.section`
  width: 56px;
  height: 100%;
  position: relative;
  border-left: 1px solid #e5e7eb;
`;

/* ---------------- ICON BUTTON ---------------- */

const IconWrap = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  button {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 2px solid #e5e7eb;
      object-fit: cover;
    }

    &:hover {
      background: #f3f4f6;
    }

    &.active {
      background: #b05408;
    }
  }
`;

/* ---------------- DROPDOWN ---------------- */

const Dropdown = styled.section`
  position: absolute;
  top: 56px;
  right: 0;
  width: 260px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(0,0,0,0.08);
  overflow: hidden;
  z-index: 9999;
`;

/* ---------------- HEADER ---------------- */

const Header = styled.div`
  background: #b05408;
  color: #fff;
  padding: 12px 14px;
  font-size: 13px;
  font-weight: 600;
`;

/* ---------------- PROFILE ---------------- */

const Profile = styled.div`
  padding: 14px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 10px;

  img {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #e5e7eb;
  }

  div {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
  }
`;

/* ---------------- MENU ---------------- */

const Menu = styled.ul`
  list-style: none;
  margin: 0;
  padding: 6px 0;
`;

const Item = styled.li`
  a {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    font-size: 13px;
    color: #374151;
    text-decoration: none;
    transition: 0.2s;

    svg {
      font-size: 14px;
      color: #6b7280;
    }

    &:hover {
      background: #f3f4f6;
      color: #111827;

      svg {
        color: #b05408;
      }
    }

    &.active {
      background: #e5e7eb;
      font-weight: 600;
    }
  }
`;

/* ---------------- COMPONENT ---------------- */

const AccountSection = memo(({ fullName, profilePhoto }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const toggle = (e) => {
    e.preventDefault();
    setOpen((p) => !p);
  };

  const close = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", close, true);
    return () => document.removeEventListener("click", close, true);
  }, [close]);

  return (
    <Wrapper ref={ref}>
      <IconWrap>
        <button onClick={toggle} className={open ? "active" : ""}>
          <Image
            source={profilePhoto || "/default-profile-photo.png"}
            alt={fullName}
          />
        </button>
      </IconWrap>

      {open && (
        <Dropdown>
          <Header>My Account</Header>

          <Profile>
            <Image
              source={profilePhoto || "/default-profile-photo.png"}
              alt={fullName}
            />
            <div>{fullName || "User"}</div>
          </Profile>

          <Menu>
            <Item>
              <NavLink to="/profile">
                <FontAwesomeIcon icon={faUser} />
                Profile
              </NavLink>
            </Item>

            <Item>
              <NavLink to="/change-password">
                <FontAwesomeIcon icon={faKey} />
                Change Password
              </NavLink>
            </Item>

            <Item>
              <NavLink to="/logs">
                <FontAwesomeIcon icon={faFileLines} />
                Logs
              </NavLink>
            </Item>

            <Item>
              <NavLink to="/sessions">
                <FontAwesomeIcon icon={faDisplay} />
                Sessions
              </NavLink>
            </Item>
          </Menu>
        </Dropdown>
      )}
    </Wrapper>
  );
});

export default AccountSection;